import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, UserCheck, CreditCard, Award, CheckCircle2, ArrowRight, Clock, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqs } from '../mockData';

export const Admission = () => {
  const admissionSteps = [
    {
      step: 1,
      icon: FileText,
      title: 'Solicitud de Información',
      description: 'Contáctanos para recibir orientación sobre carreras, modalidades y requisitos.'
    },
    {
      step: 2,
      icon: UserCheck,
      title: 'Evaluación de Perfil',
      description: 'Evaluamos tu perfil académico y/o experiencia laboral para determinar la mejor opción.'
    },
    {
      step: 3,
      icon: FileText,
      title: 'Presentación de Documentos',
      description: 'Entrega de documentación requerida para formalizar tu inscripción.'
    },
    {
      step: 4,
      icon: CreditCard,
      title: 'Matrícula',
      description: 'Realizas el pago de matrícula y quedas oficialmente inscrito.'
    },
    {
      step: 5,
      icon: Award,
      title: 'Inicio de Clases',
      description: '¡Comienzas tu formación técnica profesional!'
    }
  ];

  const requirements = [
    'Documento Nacional de Identidad (DNI) - copia simple',
    'Certificado de estudios secundarios completos',
    '2 fotografías a color tamaño carnet',
    'Partida de nacimiento (original)',
    'Ficha de matrícula (proporcionada por la institución)'
  ];

  const convalidtionRequirements = [
    'Todos los documentos de ingreso regular',
    'Certificados de trabajo (mínimo 3 años de experiencia)',
    'Constancias laborales con funciones realizadas',
    'Cartas de recomendación de empleadores (opcional)',
    'Certificados de capacitación o cursos relacionados (si los hubiera)'
  ];

  const titulationProcess = [
    {
      title: 'Culminación de Estudios',
      description: 'Aprobar todos los cursos y prácticas pre-profesionales del programa.'
    },
    {
      title: 'Proyecto de Titulación',
      description: 'Desarrollar y sustentar un proyecto técnico aplicado a tu especialidad.'
    },
    {
      title: 'Trámite Administrativo',
      description: 'Presentación de documentos y solicitud formal de titulación.'
    },
    {
      title: 'Obtención del Título',
      description: 'Recepción de tu Título Técnico Profesional a Nombre de la Nación.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-red-50 py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Admisión y Titulación</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Conoce el proceso para ingresar a MIT y obtener tu título técnico profesional
          </p>
        </div>
      </section>

      {/* Proceso de Admisión */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Proceso de Admisión</h2>
            <p className="text-xl text-gray-600">Pasos simples para iniciar tu formación profesional</p>
          </div>

          <div className="max-w-5xl mx-auto">
            {admissionSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={step.step} className={`flex gap-6 mb-12 animate-fade-in-up animate-delay-${index * 100}`}>
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-mit-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {step.step}
                    </div>
                  </div>
                  <Card className="flex-1 hover-lift">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-mit-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-mit-blue" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                          <p className="text-gray-700">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requisitos */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Requisitos de Admisión</h2>
            <p className="text-xl text-gray-600">Documentación necesaria para tu inscripción</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Ingreso Regular */}
            <Card className="hover-lift">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-mit-blue/10 rounded-xl flex items-center justify-center mb-6">
                  <FileText className="w-7 h-7 text-mit-blue" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Ingreso Regular</h3>
                <ul className="space-y-4">
                  {requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Convalidción */}
            <Card className="hover-lift border-2 border-mit-red">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-mit-red/10 rounded-xl flex items-center justify-center mb-6">
                  <Award className="w-7 h-7 text-mit-red" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Convalidción</h3>
                <ul className="space-y-4">
                  {convalidtionRequirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Modalidades */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Modalidades de Estudio</h2>
            <p className="text-xl text-gray-600">Elige la modalidad que mejor se adapte a tu ritmo de vida</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="hover-lift">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-mit-blue/10 rounded-xl flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-mit-blue" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Presencial</h3>
                <p className="text-gray-600 mb-6">
                  Clases en nuestras instalaciones con acceso a laboratorios y talleres equipados.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Horario diurno y nocturno</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Interacción directa con docentes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-mit-blue flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Prácticas en talleres</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover-lift">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-mit-red/10 rounded-xl flex items-center justify-center mb-6">
                  <Calendar className="w-7 h-7 text-mit-red" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Semi-Presencial</h3>
                <p className="text-gray-600 mb-6">
                  Combina clases presenciales con actividades virtuales. Ideal si trabajas.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Flexibilidad horaria</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Material digital disponible</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-mit-red flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Clases presenciales clave</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Proceso de Titulación */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-red-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Proceso de Titulación</h2>
            <p className="text-xl text-gray-600">Pasos para obtener tu título a nombre de la nación</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {titulationProcess.map((item, index) => (
              <Card key={index} className="hover-lift">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-mit-blue rounded-full flex items-center justify-center text-white text-xl font-bold mb-4">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-12">
            <Card className="bg-white">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <Award className="w-8 h-8 text-mit-red flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Título a Nombre de la Nación</h3>
                    <p className="text-gray-700 leading-relaxed">
                      El título que obtienes tiene validez oficial en todo el territorio peruano y es reconocido por el Estado. Te permite ejercer tu profesión técnica en el sector público y privado, así como continuar estudios superiores.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Preguntas Frecuentes */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Preguntas Frecuentes</h2>
            
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-mit-blue text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">¿Listo para Comenzar?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Inicia tu proceso de admisión hoy y transforma tu futuro profesional
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="bg-white text-mit-blue hover:bg-gray-100 w-full sm:w-auto">
                Solicitar Información
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a
              href="https://wa.me/51944748443?text=Hola, visité la web y me gustaría mas información."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-mit-blue w-full sm:w-auto">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};